/**
 * 고정(행사) 프로그램 단일 목록 — 학사일정 하단 표 · 연간시간표 '고정 프로그램 일괄 관리' · 기존 '고정 프로그램 관리'가 같은 데이터를 본다.
 * - 날짜·이름·교시·적용클래스가 모두 있으면 '확정' → 연간시간표 화면에서 해당 클래스 셀에 반영(applied 기록).
 * - 하나라도 비면 '미확정(초안)' → 목록에서만 관리, 시간표에는 반영하지 않음.
 * - 기존 모달로 직접 등록한 행사(셀)는 연간시간표 화면이 열릴 때 이 목록으로 자동 편입(source 'cell')되고, 이후 양방향으로 맞춰진다.
 * 저장: localStorage(즉시) + workspaces.fixed_program_drafts(jsonb). 컬럼이 없으면(마이그레이션 미적용) 로컬만 쓰고,
 *       컬럼이 생기면 다음 동기화 때 로컬 데이터를 자동으로 올린다(읽기→병합→쓰기, 항목별 updatedAt 최신 우선, 삭제는 묘비 표시).
 */
import { supabase } from '../supabaseClient';
import type { ClassScheduleRow, ClassScheduleSettings } from '../services/sectionLayoutService';

/** `class:1`(1교시) · `after_school:2`(방과후2) */
export type FixedDraftPeriodKey = string;

export type FixedProgramDraft = {
  id: string;
  date: string;
  name: string;
  periods: FixedDraftPeriodKey[];
  /** 편성 시간 수. 비어 있으면 선택 교시 수 */
  hours?: number;
  /** 공통 시간표명(교시별 이름이 없을 때 기본값, 비면 '교과' — 기존 행사 등록 모달 기본값과 동일) — 이전 데이터 호환 */
  timetableName: string;
  /** 교시별 시간표명(`class:2` → '교과'), 비어 있으면 공통 시간표명 */
  timetableNames?: Record<string, string>;
  /** 고정 프로그램 색(고정 팔레트), 비면 팔레트 첫 색 */
  colorHex?: string;
  /** 문서에 시간만 있을 때(교시 미정) 참고용 */
  startTime: string;
  endTime: string;
  gradeIds: string[];
  /** ai: AI 자료 입력 · manual: 표에서 추가 · cell: 기존 모달로 등록된 셀에서 편입 */
  source: 'ai' | 'manual' | 'cell';
  updatedAt: number;
  /** 연간시간표에 반영된 상태(클래스별) — 내용이 바뀌면 다시 반영 */
  applied?: Record<string, { sig: string; cellKeys: string[] }>;
  /** 삭제(묘비) — 다른 기기에도 삭제가 전달되도록 일정 기간 보관 */
  deleted?: boolean;
};

/** 교시별 시간표명을 비워 둘 때 쓰는 기본값(기존 '고정 프로그램(행사) 등록' 모달의 DEFAULT_EVENT_TIMETABLE_CELL_LABEL 과 동일) */
export const DEFAULT_FIXED_TIMETABLE_NAME = '교과';

export const FIXED_DRAFTS_CHANGE_EVENT = 'cp-fixed-program-drafts-change';
const KEY = (ws: string) => `cp-fixed-program-drafts:${ws}`;
const TOMBSTONE_TTL_MS = 60 * 24 * 60 * 60 * 1000;
const RETRY_AFTER_MS = 5 * 60 * 1000;

export function newDraftId(): string {
  return `fd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 편성 시간 수(N시간): hours 가 있으면 그 값, 없으면 선택 교시 수 */
export function draftHours(d: Pick<FixedProgramDraft, 'hours' | 'periods'>): number {
  return d.hours && d.hours > 0 ? d.hours : d.periods.length;
}

function sanitize(list: unknown): FixedProgramDraft[] {
  return Array.isArray(list)
    ? (list as FixedProgramDraft[]).filter((d) => d && typeof d.id === 'string' && Array.isArray(d.periods) && Array.isArray(d.gradeIds))
    : [];
}

export function loadFixedDrafts(workspaceId: string): FixedProgramDraft[] {
  try {
    return sanitize(JSON.parse(localStorage.getItem(KEY(workspaceId)) || '[]'));
  } catch {
    return [];
  }
}

/** 화면에 보이는 항목(삭제 묘비 제외) */
export function liveDrafts(list: FixedProgramDraft[]): FixedProgramDraft[] {
  return list.filter((d) => !d.deleted);
}

function writeLocal(workspaceId: string, drafts: FixedProgramDraft[]) {
  try {
    localStorage.setItem(KEY(workspaceId), JSON.stringify(drafts));
  } catch {
    /* 용량 초과 등 */
  }
}

function mergeDrafts(a: FixedProgramDraft[], b: FixedProgramDraft[]): FixedProgramDraft[] {
  const byId = new Map<string, FixedProgramDraft>();
  [...a, ...b].forEach((d) => {
    const prev = byId.get(d.id);
    if (!prev || (d.updatedAt ?? 0) >= (prev.updatedAt ?? 0)) byId.set(d.id, d);
  });
  const now = Date.now();
  return Array.from(byId.values()).filter((d) => !(d.deleted && now - (d.updatedAt ?? 0) > TOMBSTONE_TTL_MS));
}

/** 서버 컬럼 상태: 없으면 잠시 뒤 다시 확인(마이그레이션 적용 즉시 자동 업로드) */
let serverMissingAt = 0;
const serverOk = () => !serverMissingAt || Date.now() - serverMissingAt > RETRY_AFTER_MS;
function noteServerError(error: { message?: string; code?: string } | null): boolean {
  if (!error) return false;
  if (/fixed_program_drafts|column|PGRST204|42703/i.test(`${error.code ?? ''} ${error.message ?? ''}`)) serverMissingAt = Date.now();
  return true;
}

export type FixedDraftsServerState = 'synced' | 'local_only' | 'error';
let lastServerState: FixedDraftsServerState = 'local_only';
export function fixedDraftsServerState(): FixedDraftsServerState {
  return lastServerState;
}

async function fetchRemote(workspaceId: string): Promise<FixedProgramDraft[] | null> {
  const { data, error } = await supabase.from('workspaces').select('fixed_program_drafts').eq('id', workspaceId).maybeSingle();
  if (noteServerError(error)) {
    lastServerState = serverMissingAt ? 'local_only' : 'error';
    return null;
  }
  return sanitize((data as { fixed_program_drafts?: unknown } | null)?.fixed_program_drafts);
}

/** 읽기→병합→쓰기(다른 기기의 최신 항목을 덮어쓰지 않도록) */
async function pushMerged(workspaceId: string): Promise<FixedProgramDraft[] | null> {
  if (!serverOk()) return null;
  const remote = await fetchRemote(workspaceId);
  if (!remote) return null;
  const merged = mergeDrafts(remote, loadFixedDrafts(workspaceId));
  if (JSON.stringify(merged) !== JSON.stringify(remote)) {
    const { error } = await supabase.from('workspaces').update({ fixed_program_drafts: merged }).eq('id', workspaceId);
    if (noteServerError(error)) {
      lastServerState = serverMissingAt ? 'local_only' : 'error';
      return null;
    }
  }
  lastServerState = 'synced';
  lastServerSyncAt.set(workspaceId, Date.now());
  return merged;
}

const pushTimers = new Map<string, number>();
/** 같은 워크스페이스 서버 동기화를 겹치지 않게, 직후 재조회는 로컬 캐시를 재사용 */
const SYNC_REUSE_MS = 5 * 60 * 1000;
const lastServerSyncAt = new Map<string, number>();
const syncInflight = new Map<string, Promise<FixedProgramDraft[]>>();
export function saveFixedDrafts(workspaceId: string, drafts: FixedProgramDraft[]): void {
  writeLocal(workspaceId, drafts);
  window.dispatchEvent(new CustomEvent(FIXED_DRAFTS_CHANGE_EVENT, { detail: { workspaceId } }));
  const t = pushTimers.get(workspaceId);
  if (t != null) window.clearTimeout(t);
  pushTimers.set(
    workspaceId,
    window.setTimeout(() => {
      pushTimers.delete(workspaceId);
      void pushMerged(workspaceId);
    }, 800)
  );
}

/** 서버와 병합(로컬에만 있는 항목은 자동 업로드). 바뀌었으면 변경 이벤트 발생 */
export async function syncFixedDraftsFromServer(
  workspaceId: string,
  opts?: { force?: boolean }
): Promise<FixedProgramDraft[]> {
  const sid = workspaceId.trim();
  if (!sid) return [];
  if (!opts?.force) {
    const at = lastServerSyncAt.get(sid) ?? 0;
    if (at && Date.now() - at < SYNC_REUSE_MS) return loadFixedDrafts(sid);
    const pending = syncInflight.get(sid);
    if (pending) return pending;
  }
  const job = (async () => {
    const before = loadFixedDrafts(sid);
    const merged = await pushMerged(sid).catch(() => null);
    if (!merged) return loadFixedDrafts(sid);
    /** 요청 도중 연간시간표 캐시에서 넣은 항목을 덮어쓰지 않도록 쓰기 직전에 다시 병합 */
    const latest = loadFixedDrafts(sid);
    const final = mergeDrafts(merged, latest);
    if (JSON.stringify(final) !== JSON.stringify(latest)) {
      writeLocal(sid, final);
      window.dispatchEvent(new CustomEvent(FIXED_DRAFTS_CHANGE_EVENT, { detail: { workspaceId: sid } }));
    } else if (JSON.stringify(final) !== JSON.stringify(before)) {
      window.dispatchEvent(new CustomEvent(FIXED_DRAFTS_CHANGE_EVENT, { detail: { workspaceId: sid } }));
    }
    lastServerSyncAt.set(sid, Date.now());
    return final;
  })().finally(() => {
    if (syncInflight.get(sid) === job) syncInflight.delete(sid);
  });
  syncInflight.set(sid, job);
  return job;
}

/** 변경 이벤트 + 창 포커스 시 서버 동기화 구독 */
export function subscribeFixedDrafts(workspaceId: string, onChange: () => void): () => void {
  const on = (e: Event) => {
    if ((e as CustomEvent<{ workspaceId: string }>).detail?.workspaceId === workspaceId) onChange();
  };
  const onFocus = () => {
    if (document.visibilityState === 'visible') void syncFixedDraftsFromServer(workspaceId);
  };
  window.addEventListener(FIXED_DRAFTS_CHANGE_EVENT, on);
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);
  void syncFixedDraftsFromServer(workspaceId);
  return () => {
    window.removeEventListener(FIXED_DRAFTS_CHANGE_EVENT, on);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
  };
}

export function updateFixedDrafts(workspaceId: string, fn: (prev: FixedProgramDraft[]) => FixedProgramDraft[]): FixedProgramDraft[] {
  const next = fn(loadFixedDrafts(workspaceId));
  saveFixedDrafts(workspaceId, next);
  return next;
}

export type DraftMissing = { date: boolean; name: boolean; periods: boolean; grades: boolean };
export function draftMissing(d: FixedProgramDraft): DraftMissing {
  return {
    date: !/^\d{4}-\d{2}-\d{2}$/.test(d.date),
    name: !d.name.trim(),
    periods: d.periods.length === 0 || (!!d.hours && d.periods.length < d.hours),
    grades: d.gradeIds.length === 0,
  };
}
export function isDraftConfirmed(d: FixedProgramDraft): boolean {
  const m = draftMissing(d);
  return !d.deleted && !m.date && !m.name && !m.periods && !m.grades;
}

export function draftTimetableName(d: FixedProgramDraft): string {
  return d.timetableName.trim() || DEFAULT_FIXED_TIMETABLE_NAME;
}

/** 교시별 실제 시간표명 */
export function draftPeriodName(d: Pick<FixedProgramDraft, 'timetableName' | 'timetableNames'>, periodKey: string): string {
  return (d.timetableNames?.[periodKey] ?? '').trim() || d.timetableName.trim() || DEFAULT_FIXED_TIMETABLE_NAME;
}

/** 반영 서명(이 값이 바뀌면 연간시간표에 다시 반영) */
export function draftApplySig(d: FixedProgramDraft): string {
  const periods = [...d.periods].sort();
  return JSON.stringify([d.date, d.name.trim(), periods, periods.map((k) => draftPeriodName(d, k)), (d.colorHex ?? '').trim().toLowerCase()]);
}

export function periodKeyLabel(key: string): string {
  const [k, n] = key.split(':');
  return k === 'after_school' ? `방과후${n}` : `${n}교시`;
}
export function comparePeriodKeys(a: string, b: string): number {
  const r = (k: string) => (k.startsWith('after_school:') ? 100 : 0) + Number(k.split(':')[1] || 0);
  return r(a) - r(b);
}

/** 클래스의 수업시간 행(섹션 등록 기준) */
export function scheduleRowsForGrade(settings: ClassScheduleSettings | null, gradeId: string): ClassScheduleRow[] {
  if (!settings) return [];
  const sec = settings.sections.find((s) => s.gradeIds.includes(gradeId)) ?? settings.sections.find((s) => s.id === 'base');
  return (sec && settings.sectionRows[sec.id]) || [];
}

/** 교시 목록과 시각 */
export function periodOptions(rows: ClassScheduleRow[]): Array<{ key: string; label: string; start: string; end: string }> {
  let c = 0;
  let a = 0;
  const out: Array<{ key: string; label: string; start: string; end: string }> = [];
  rows.forEach((r) => {
    if (r.kind === 'class') {
      c += 1;
      out.push({ key: `class:${c}`, label: `${c}교시`, start: r.startTime, end: r.endTime });
    } else if (r.kind === 'after_school') {
      a += 1;
      out.push({ key: `after_school:${a}`, label: `방과후${a}`, start: r.startTime, end: r.endTime });
    }
  });
  return out;
}

/** 선택 교시 → 편성시간 'HH:MM~HH:MM' */
export function periodsTimeRange(keys: string[], opts: ReturnType<typeof periodOptions>): string {
  const sel = opts.filter((o) => keys.includes(o.key));
  const starts = sel.map((o) => o.start).filter(Boolean).sort();
  const ends = sel.map((o) => o.end).filter(Boolean).sort();
  if (!starts.length || !ends.length) return '';
  return `${starts[0]}~${ends[ends.length - 1]}`;
}

/**
 * 저장 여부 비교용 정규화 서명 — updatedAt·applied 같은 휘발성 필드는 무시하고,
 * 빈 시간표명과 기본값 '교과'를 같게 보며(교시별 실제 적용 이름으로 비교), 교시·클래스 순서를 정렬한다.
 * 편집 후 원래 값으로 되돌리면 저장본과 같은 서명이 된다.
 */
export function fixedDraftCompareSig(d: FixedProgramDraft): string {
  const periods = [...(d.periods ?? [])].map(String).sort(comparePeriodKeys);
  return JSON.stringify({
    id: d.id,
    deleted: !!d.deleted,
    date: d.date || '',
    name: (d.name || '').trim(),
    periods,
    names: periods.map((pk) => draftPeriodName(d, pk)),
    hours: draftHours(d),
    color: (d.colorHex || '').toLowerCase(),
    start: d.startTime || '',
    end: d.endTime || '',
    grades: [...(d.gradeIds ?? [])].sort(),
  });
}

/** 목록 비교 서명 — 삭제(묘비)된 항목은 제외, id 순 정렬 */
export function fixedDraftsCompareSig(list: FixedProgramDraft[]): string {
  return list
    .filter((d) => !d.deleted)
    .map(fixedDraftCompareSig)
    .sort()
    .join('\n');
}
